import { Info, TriangleAlert } from 'lucide-react';
import { useRef, useState } from 'react';

import {
  DEVICE_ALERT_SEVERITY_TEXT_CLASS,
  DEVICE_ALERT_TOOLTIP_DELAY_MS,
} from '../../constants/device-alerts';
import { useIntl } from '../../hooks';
import {
  describeDeviceAlert,
  highestDeviceAlertSeverity,
  sortDeviceAlertsBySeverity,
} from '../../lib/device-alert-text';
import { cn } from '../../lib/utils';
import type { DeviceAlert } from '../../types';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui';
import { DeviceAlertPopover } from './DeviceAlertPopover';

type DeviceAlertIndicatorProps = {
  deviceId: string;
  deviceSerial: string;
  alerts: DeviceAlert[];
  canAcknowledge: boolean;
  animated: boolean;
};

export const DeviceAlertIndicator = ({
  deviceId,
  deviceSerial,
  alerts,
  canAcknowledge,
  animated,
}: DeviceAlertIndicatorProps) => {
  const intl = useIntl();
  const [isHoverOpen, setIsHoverOpen] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  // Closing the popover returns focus to the trigger, and the tooltip opens on focus; nothing
  // would close it again, since the pointer never re-enters. That one open is swallowed.
  const suppressNextTooltipOpenRef = useRef(false);

  if (alerts.length === 0) {
    return null;
  }

  const severity = highestDeviceAlertSeverity(alerts);
  const SeverityIcon = severity === 'info' ? Info : TriangleAlert;
  const orderedAlerts = sortDeviceAlertsBySeverity(alerts);

  const handleTooltipOpenChange = (open: boolean) => {
    if (open && suppressNextTooltipOpenRef.current) {
      suppressNextTooltipOpenRef.current = false;
      return;
    }

    setIsHoverOpen(open);
  };

  const handlePopoverOpenChange = (open: boolean) => {
    if (!open) {
      suppressNextTooltipOpenRef.current = true;
    }

    setIsPopoverOpen(open);
  };

  const clearTooltipSuppression = () => {
    suppressNextTooltipOpenRef.current = false;
  };

  return (
    <TooltipProvider delayDuration={DEVICE_ALERT_TOOLTIP_DELAY_MS}>
      <Popover open={isPopoverOpen} onOpenChange={handlePopoverOpenChange}>
        <Tooltip open={isHoverOpen && !isPopoverOpen} onOpenChange={handleTooltipOpenChange}>
          {/* The attributes live on the outer span: Radix owns data-state on the trigger itself. */}
          <span
            data-test-id="device-alert-indicator"
            data-severity={severity}
            data-count={alerts.length}
            className={cn(
              'inline-flex h-8 items-center pr-2',
              DEVICE_ALERT_SEVERITY_TEXT_CLASS[severity],
            )}
          >
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label={intl.formatMessage(
                    { id: 'alerts.indicatorLabel' },
                    { count: alerts.length },
                  )}
                  // A pointer arriving is a fresh hover, never the focus return being suppressed.
                  onPointerEnter={clearTooltipSuppression}
                  onPointerLeave={clearTooltipSuppression}
                  onBlur={clearTooltipSuppression}
                  className={cn(
                    'relative inline-flex cursor-pointer rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    animated && 'motion-safe:animate-suppression-pulse',
                  )}
                >
                  <SeverityIcon className="h-4 w-4" />
                  {/* A plain span, never a Badge: a badge class here shifts the card's badge indexes. */}
                  {alerts.length > 1 && (
                    <span className="absolute -top-1.5 -right-2 text-xs leading-none font-semibold tabular-nums">
                      {alerts.length}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
            </TooltipTrigger>
          </span>
          <TooltipContent side="right" className="max-w-xs">
            <div data-test-id="device-alert-lines">
              {orderedAlerts.map((deviceAlert) => (
                <p key={deviceAlert.id}>{describeDeviceAlert(intl, deviceAlert)}</p>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
        <PopoverContent
          side="bottom"
          align="start"
          aria-label={intl.formatMessage({ id: 'alerts.popover.label' })}
        >
          <DeviceAlertPopover
            deviceId={deviceId}
            deviceSerial={deviceSerial}
            alerts={alerts}
            canAcknowledge={canAcknowledge}
          />
        </PopoverContent>
      </Popover>
    </TooltipProvider>
  );
};
